import { defineStore } from 'pinia';
import { fetchKioskConfig } from '@/lib/request/fetchKioskConfig';
import { KioskConfig } from '@/lib/request/types/kioskConfig';
import { BreakType } from '@/stores/timesheets';
import KioskAuthenticationMode from '@/lib/request/types/KioskAuthenticationMode';

export interface KioskConfigStoreStatus {
  isLoading: boolean;
}

export interface KioskConfigStoreErrors {
  loadingError: Error | null;
}

export interface KioskConfigStore {
  data: KioskConfig;
  status: KioskConfigStoreStatus;
  errors: KioskConfigStoreErrors;
}

export const useKioskConfigStore = defineStore({
  id: 'kioskConfig',
  state: (): KioskConfigStore => ({
    data: {} as KioskConfig,
    status: {
      isLoading: false,
    },
    errors: {
      loadingError: null,
    },
  }),
  getters: {
    timesheetClosestBlock(state): number {
      return parseInt(state.data.KioskAppConfigs.TIMESHEET_CLOSEST_BLOCK);
    },
    locationId(state) {
      return state.data.KioskCompanyId;
    },
    getPaidBreakInformation(state) {
      return state.data.KioskAppConfigs.SHIFT_TEMPLATE_SLOTS_CONFIG.find(
        ({ SlotValue, SlotName }) => {
          if (SlotValue == BreakType.Paid) {
            return SlotName;
          }
        }
      );
    },
    getUnpaidBreakInformation(state) {
      return state.data.KioskAppConfigs.SHIFT_TEMPLATE_SLOTS_CONFIG.find(
        ({ SlotValue, SlotName }) => {
          if (SlotValue == BreakType.Unpaid) {
            return SlotName;
          }
        }
      );
    },
    getAuthenticationMode(state) {
      const defaultKioskAuthenticationName = 'UNKNOWN_AUTH_TYPE';

      return (
        KioskAuthenticationMode[state.data.KioskAppConfigs.AUTHENTICATE_MODE] ||
        defaultKioskAuthenticationName
      );
    },
  },
  actions: {
    async load() {
      this.errors.loadingError = null;
      this.status.isLoading = true;
      try {
        this.data = await fetchKioskConfig();
      } catch (e) {
        if (e instanceof Error) {
          this.errors.loadingError = e;
          throw e;
        }
      } finally {
        this.status.isLoading = false;
      }
    },
  },
});
